/**
 * ExportButton
 *
 * One place that knows how exporting works: it posts the rows on screen,
 * hands the file to the browser, and reports what happened. Pages just say
 * what to export.
 *
 * It carries its own feedback because the pages that export do not all have
 * a toast, and an export that silently does nothing is the worst outcome.
 * A plan ceiling gets a popover anchored to this button rather than a toast
 * or a jump to the pricing page: the person is mid-task, they did nothing
 * wrong, and the answer belongs beside the thing they just clicked.
 */

import React, { useRef, useState } from 'react';
import { Button, Snackbar, Alert as MuiAlert, Tooltip } from '@mui/material';
import { Icon } from '@iconify/react';
import { requestExport } from '../../services/exportData';
import { isPlanLimit } from '../../utils/planLimits';
import PlanLimitPopover from './PlanLimitPopover';

function ExportButton({
  filename,
  headers,
  rows,
  format = 'csv',
  label = 'Export',
  size = 'small',
  variant = 'outlined',
  sx,
  disabled,
}) {
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  // A plan limit is explained beside the button rather than by a toast in the
  // corner, or worse a jump to the pricing page: the person is mid-task.
  const [limit, setLimit] = useState(null);
  const anchorRef = useRef(null);

  const count = Array.isArray(rows) ? rows.length : 0;
  const nothingToExport = count === 0;

  const run = async () => {
    if (busy || nothingToExport) return;
    setBusy(true);
    try {
      const result = await requestExport(filename, headers, rows, format);
      setToast({
        severity: 'success',
        msg: `Exported ${result.rows.toLocaleString()} rows.`,
      });
    } catch (err) {
      if (isPlanLimit(err)) {
        setLimit(err.message || 'Exporting is part of a paid plan.');
      } else {
        setToast({ severity: 'error', msg: err?.message || 'Could not export.' });
      }
    } finally {
      setBusy(false);
    }
  };

  const button = (
    <span ref={anchorRef}>
      <Button
        variant={variant}
        size={size}
        disabled={busy || nothingToExport || disabled}
        startIcon={<Icon icon="ph:download-simple" width={16} height={16} />}
        onClick={run}
        sx={sx}
      >
        {busy ? 'Preparing…' : label}
      </Button>
    </span>
  );

  return (
    <>
      {nothingToExport ? (
        <Tooltip title="Nothing to export with the current filters">{button}</Tooltip>
      ) : (
        button
      )}
      <PlanLimitPopover
        anchorEl={anchorRef.current}
        open={!!limit}
        onClose={() => setLimit(null)}
        title="Export is part of a paid plan"
        message={limit || ''}
      />
      <Snackbar
        open={!!toast}
        autoHideDuration={toast?.severity === 'success' ? 3000 : 8000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast && (
          <MuiAlert
            severity={toast.severity}
            onClose={() => setToast(null)}
          >
            {toast.msg}
          </MuiAlert>
        )}
      </Snackbar>
    </>
  );
}

export default ExportButton;
